/* eslint-disable */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * AnalyzingOverlay — full-screen blocking modal shown while the AI analysis
 * is running. Cannot be dismissed by the user. The parent unmounts it once
 * the underlying request resolves (success OR error), so we don't ship a
 * close button — analysis continues no matter what.
 *
 * Bilingual: reads its copy from the active language so the analysis
 * waiting experience matches the dashboard language. Cycles short
 * reassurance messages every ~6 seconds so the user can tell the system
 * is actually doing something.
 */

const MESSAGES = {
  tr: [
    "Yusi şu anda işletmenizi ve rakiplerinizi inceliyor.",
    "Web sitenizin yapay zekâlar tarafından ne kadar anlaşılabilir olduğunu kontrol ediyoruz.",
    "Hizmetleriniz, konum bilgileriniz ve içerik gücünüz analiz ediliyor.",
    "Yapay zekâların sizi hangi müşteri sorularında önerebileceğini ölçüyoruz.",
    "Rakiplerinizin dijital görünürlüğüyle karşılaştırma yapılıyor.",
    "Eksik kalan alanlar görev listesine dönüştürülüyor.",
    "AI görünürlük skorunuz ve öncelikli aksiyonlar hazırlanıyor.",
    "Raporunuz birazdan hazır olacak.",
  ],
  en: [
    "Yusi is reviewing your business and your competitors right now.",
    "We're checking how clearly AI tools can understand your website.",
    "Your services, location info and content strength are being analysed.",
    "We're measuring which customer questions AI tools could recommend you for.",
    "Comparing your visibility against your competitors.",
    "Turning your gaps into a prioritised task list.",
    "Your AI Visibility Score and priority actions are being prepared.",
    "Your report will be ready in a moment.",
  ],
};

const COPY = {
  tr: {
    title: "Yusi analiz ediyor",
    subtitle: "AI görünürlük skorunuz hazırlanıyor. Bu işlem genellikle 60-90 saniye sürebilir.",
    note: "Bu işlem web sitenizin ve rakiplerinizin durumuna göre biraz sürebilir. Lütfen sayfayı kapatmayın.",
    progress: "Analiz hazırlanıyor...",
    successTitle: "Analiz tamamlandı",
    successSubtitle: "Raporunuz hazır. Sonuç ekranına yönlendiriliyorsunuz.",
  },
  en: {
    title: "Yusi is analysing",
    subtitle: "Your AI Visibility Score is being prepared. This usually takes 60-90 seconds.",
    note: "This can take a little while depending on your website and competitors. Please don't close this page.",
    progress: "Preparing your analysis...",
    successTitle: "Analysis complete",
    successSubtitle: "Your report is ready. Redirecting you to the results screen.",
  },
};

export default function AnalyzingOverlay({ open, success = false }) {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.tr;
  const messages = MESSAGES[lang] || MESSAGES.tr;
  const [idx, setIdx] = useState(0);

  // Reset to first message every time the overlay re-opens, and rotate
  // every 6 seconds while it's visible. The rotation stops at the last
  // message (we don't loop — last message is "report will be ready soon"
  // which is the right closing note if analysis takes 90s+).
  useEffect(() => {
    if (!open || success) {
      if (!open) setIdx(0);
      return;
    }
    const id = setInterval(() => {
      setIdx((i) => (i + 1 < messages.length ? i + 1 : i));
    }, 6000);
    return () => clearInterval(id);
  }, [open, success, messages.length]);

  // Lock body scroll while overlay is open — prevents accidental scrolling
  // behind the modal on mobile.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const overlay = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
      data-testid="analyzing-overlay"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      <div className="relative w-full max-w-md rounded-3xl glass-strong border border-cyan-500/20 p-6 sm:p-8 overflow-hidden">
        {/* Soft brand glow */}
        <div className="absolute -top-20 -right-20 w-56 h-56 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-purple-500/15 blur-3xl rounded-full pointer-events-none" />

        <div className="relative flex flex-col items-center text-center">
          {success ? (
            <>
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 mb-4 flex items-center justify-center" data-testid="analyzing-success-icon">
                <div className="absolute inset-0 rounded-full bg-emerald-500/15 blur-2xl" />
                <div className="absolute inset-2 rounded-full bg-emerald-500/10 border border-emerald-400/40 animate-pulse-soft" />
                <CheckCircle2 size={56} className="relative text-emerald-300 drop-shadow-[0_0_12px_rgba(52,211,153,0.6)]" strokeWidth={1.75} />
              </div>

              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-1.5" data-testid="analyzing-success-title">
                {t.successTitle}
              </h2>
              <p className="text-sm text-slate-300 leading-relaxed max-w-xs" data-testid="analyzing-success-subtitle">
                {t.successSubtitle}
              </p>

              <div className="mt-6 w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full w-full bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full" />
              </div>

              <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-300">
                <Loader2 size={11} className="animate-spin" />
                {lang === "en" ? "Redirecting…" : "Yönlendiriliyor…"}
              </div>
            </>
          ) : (
            <>
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 mb-4">
                <img
                  src="/yusi/yusi-03-working-determined.png"
                  alt="Yusi"
                  className="w-full h-full object-contain animate-pulse-soft"
                  draggable="false"
                />
              </div>

              <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-1.5" data-testid="analyzing-title">
                {t.title}
              </h2>
              <p className="text-sm text-slate-300 leading-relaxed max-w-xs">
                {t.subtitle}
              </p>

              {/* Indeterminate progress bar — no fake percentage. */}
              <div className="mt-6 w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full animate-progress-slide" />
              </div>

              {/* Rotating status message */}
              <div className="mt-5 min-h-[48px] flex items-center" data-testid="analyzing-message">
                <p className="text-sm text-cyan-200 leading-relaxed max-w-xs">
                  {messages[idx]}
                </p>
              </div>

              <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400">
                <Loader2 size={11} className="animate-spin" />
                {t.progress}
              </div>

              <p className="mt-6 text-[11px] text-slate-500 leading-relaxed max-w-xs border-t border-white/5 pt-4">
                {t.note}
              </p>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes progress-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        .animate-progress-slide {
          animation: progress-slide 2.4s ease-in-out infinite;
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(0.97); }
        }
        .animate-pulse-soft {
          animation: pulse-soft 2.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );

  return createPortal(overlay, document.body);
}
